import { zodResolver } from '@hookform/resolvers/zod';
import { EyeIcon, EyeOffIcon, Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { useUserLogin } from '~shared/api/login';
import { Button } from '~shared/shadcn/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~shared/shadcn/ui/form';
import { Input } from '~shared/shadcn/ui/input';

import { ForgotPasswordConfirmation } from './forgot-password-confirmation';

const loginSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Email is required')
    .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

interface LoginFormProps {
  locationId: string;
  redirect?: string;
}

export const LoginForm = ({ locationId, redirect }: LoginFormProps) => {
  const [showForgotPasswordDialog, setShowForgotPasswordDialog] =
    useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const loginMutation = useUserLogin();

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const emailValue = form.watch('email');
  const emailError = form.formState.errors.email;
  const isEmailValid = useMemo(() => {
    if (!emailValue || emailValue.trim().length === 0) {
      return false;
    }
    // Validate email format using the same regex as the schema
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailValue.trim()) && !emailError;
  }, [emailValue, emailError]);

  const handleForgotPassword = async () => {
    // Trigger validation on the email field
    const emailValidation = await form.trigger('email');

    if (!emailValidation || !isEmailValid) {
      toast.error('Please enter a valid email address');
      return;
    }

    setShowForgotPasswordDialog(true);
  };

  const onSubmit = async (values: LoginFormValues) => {
    try {
      await loginMutation.mutateAsync({
        email: values.email,
        password: values.password,
        locationId,
        redirect,
      });
    } catch (error) {
      // Error is already handled by the mutation's onError
      console.error('Login failed:', error);
    }
  };

  return (
    <>
      <div className='w-full'>
        <div className='mb-8 text-center'>
          <h2 className='text-2xl font-semibold text-[#24292f]'>
            Welcome Back
          </h2>
          <p className='mt-2 text-sm text-[#57606a]'>
            Sign in to your account to continue.
          </p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-5'>
            <FormField
              control={form.control}
              name='email'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type='email'
                      placeholder='Enter your email'
                      autoComplete='email'
                      className='h-11 rounded-md border border-[#d0d7de] bg-white px-4 text-base text-[#24292f] transition-colors placeholder:text-[#8b949e] hover:border-[#afb8c1] focus:border-[#FFC300] focus:outline-none focus:ring-2 focus:ring-[#FFC300]/40 focus-visible:ring-[#FFC300]/40'
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='password'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <div className='relative'>
                      <Input
                        {...field}
                        type={showPassword ? 'text' : 'password'}
                        placeholder='Enter your password'
                        autoComplete='current-password'
                        className='h-11 rounded-md border border-[#d0d7de] bg-white px-4 text-base text-[#24292f] transition-colors placeholder:text-[#8b949e] hover:border-[#afb8c1] focus:border-[#FFC300] focus:outline-none focus:ring-2 focus:ring-[#FFC300]/40 focus-visible:ring-[#FFC300]/40'
                      />
                      <button
                        type='button'
                        onClick={() => {
                          setShowPassword(!showPassword);
                        }}
                        className='absolute right-3 top-1/2 -translate-y-1/2 text-[#57606a] transition-colors hover:text-[#24292f]'
                        aria-label={
                          showPassword ? 'Hide password' : 'Show password'
                        }
                      >
                        {showPassword ? (
                          <EyeOffIcon className='size-4' />
                        ) : (
                          <EyeIcon className='size-4' />
                        )}
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className='flex items-center justify-end'>
              <Button
                type='button'
                variant='link'
                onClick={handleForgotPassword}
                disabled={!isEmailValid}
                className='h-fit p-0 text-sm text-[#57606a] transition-colors hover:text-[#24292f] hover:underline'
              >
                Forgot password?
              </Button>
            </div>

            <Button
              type='submit'
              className='h-12 w-full'
              disabled={loginMutation.isPending}
            >
              {loginMutation.isPending ? (
                <>
                  <Loader2 className='mr-2 size-4 animate-spin' />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
          <div className='mt-10 border-t border-[#d0d7de] pt-6'>
            <p className='text-center text-sm text-[#57606a]'>
              New staff member?{' '}
              <span className='text-[#24292f]'>
                Ask your studio admin to invite you.
              </span>
            </p>
          </div>
        </Form>
      </div>
      {showForgotPasswordDialog && isEmailValid && (
        <ForgotPasswordConfirmation
          open={showForgotPasswordDialog}
          onOpenChange={setShowForgotPasswordDialog}
          email={emailValue}
          locationId={locationId}
        />
      )}
    </>
  );
};
